/**
 * ELP Projects archive — client-side subject / country filtering + load-more.
 *
 * All project cards render up front (each carries `data-subjects` and
 * `data-countries` term-slug lists). Filtering shows/hides cards; a batch limit
 * (data-elp-batch) keeps only the first N matches visible with a Load More
 * button. When a country is selected, each shown card also lists the team
 * members from that country. Visibility is toggled via inline display (not the
 * `hidden` attribute) because the card's CSS `display:flex` would override it.
 */
export function initElpArchiveFilters() {
	const grid = document.querySelector( '[data-elp-grid]' );
	if ( ! grid ) {
		return;
	}

	const cards   = Array.from( grid.querySelectorAll( '.whf-elp-archive__card' ) );
	const filters = document.querySelector( '[data-elp-filters]' );
	const selects = filters ? Array.from( filters.querySelectorAll( '[data-elp-filter]' ) ) : [];
	const empty    = document.querySelector( '[data-elp-empty]' );
	const moreWrap = document.querySelector( '[data-elp-more-wrap]' );
	const moreBtn  = document.querySelector( '[data-elp-more]' );
	const batch    = parseInt( grid.getAttribute( 'data-elp-batch' ), 10 ) || 21;
	const countrySelect = selects.find( ( s ) => s.getAttribute( 'data-elp-filter' ) === 'countries' );

	let shown = batch;

	const matches = ( card ) =>
		selects.every( ( select ) => {
			if ( ! select.value ) {
				return true;
			}
			const key   = select.getAttribute( 'data-elp-filter' ); // subjects | countries
			const slugs = ( card.getAttribute( 'data-' + key ) || '' ).split( ' ' );
			return slugs.includes( select.value );
		} );

	// Show/hide the team members from the selected country on a card.
	const updateMembers = ( card, country, countryName ) => {
		const wrap = card.querySelector( '[data-card-members]' );
		if ( ! wrap ) {
			return;
		}
		if ( ! country ) {
			wrap.hidden = true;
			return;
		}
		let any = false;
		wrap.querySelectorAll( '.whf-elp-archive__card-member' ).forEach( ( m ) => {
			const show = m.getAttribute( 'data-country' ) === country;
			m.style.display = show ? '' : 'none';
			if ( show ) {
				any = true;
			}
		} );
		const label = wrap.querySelector( '[data-members-label]' );
		if ( label ) {
			label.textContent = any ? countryName : '';
		}
		wrap.hidden = ! any;
	};

	const render = () => {
		const country     = countrySelect ? countrySelect.value : '';
		const countryName = country ? countrySelect.options[ countrySelect.selectedIndex ].text : '';
		let matched = 0;
		cards.forEach( ( card ) => {
			if ( matches( card ) ) {
				matched++;
				card.style.display = matched <= shown ? '' : 'none';
			} else {
				card.style.display = 'none';
			}
			updateMembers( card, country, countryName );
		} );
		if ( empty ) {
			empty.hidden = matched !== 0;
		}
		if ( moreWrap ) {
			moreWrap.hidden = matched <= shown;
		}
	};

	selects.forEach( ( select ) =>
		select.addEventListener( 'change', () => {
			shown = batch;
			render();
		} )
	);

	if ( moreBtn ) {
		moreBtn.addEventListener( 'click', () => {
			shown += batch;
			render();
		} );

		// Auto-reveal the next batch as the button scrolls into view, matching
		// the site-wide load-more behaviour. The counter caps consecutive
		// auto-clicks and resets when the button leaves the viewport, so
		// scrolling keeps revealing while a broken state can't loop forever.
		if ( 'IntersectionObserver' in window ) {
			let autoReveals = 0;
			const autoObserver = new IntersectionObserver(
				( entries ) => {
					entries.forEach( ( entry ) => {
						if ( ! entry.isIntersecting ) {
							autoReveals = 0;
							return;
						}
						if ( ( moreWrap && moreWrap.hidden ) || autoReveals >= 3 ) {
							return;
						}
						autoReveals += 1;
						moreBtn.click();
					} );
				},
				{ rootMargin: '600px' }
			);
			autoObserver.observe( moreBtn );
		}
	}

	// Pre-filter from the URL (?subject=slug / ?country=slug) — clicking a topic
	// or country on a project page lands here with that filter applied.
	const params = new URLSearchParams( window.location.search );
	// Prefixed params avoid colliding with the registered `country` / taxonomy
	// query vars (which would route to a term archive instead).
	const urlParamFor = { subjects: 'elp_subject', countries: 'elp_country' };
	selects.forEach( ( select ) => {
		const param = urlParamFor[ select.getAttribute( 'data-elp-filter' ) ];
		if ( ! param ) {
			return;
		}
		const value = params.get( param );
		if ( value && Array.from( select.options ).some( ( o ) => o.value === value ) ) {
			select.value = value;
		}
	} );

	render();
}
